/**
 * tax-schema.ts — ทะเบียนภาษี/การยื่นแบบ/หนังสือรับรอง + reversal (CONTEXT §4 Tax panel, §5.8, T067)
 *
 * กติกา:
 * - tax_ledger.pnl_impact = false โดย default (VAT/WHT ไม่ลดกำไรอัตโนมัติ)
 *   กระทบ P&L เฉพาะรายการที่ confirmed (ฝ่ายบัญชียืนยัน)
 * - Corporate income tax (kind='cit') ใช้ยอดจริง/adjustment ที่ confirmed
 *   ห้ามคำนวณ EBITDA × 20% อัตโนมัติ (report อ่าน tax_ledger kind=cit pnl_impact=true)
 * - reversal ของ financial_entry link กลับต้นฉบับและกลับเครื่องหมายยอด
 */
import { z } from 'zod';
import { moneyString, uuid, dateKey } from './schemas.js';
import { vatClaimability, whtType } from './entry-schema.js';

export const taxLedgerKind = z.enum([
  'output_vat',
  'input_vat',
  'wht',
  'cit',
  'penalty',
  'surcharge',
  'other',
]);
export type TaxLedgerKind = z.infer<typeof taxLedgerKind>;

export const taxFormType = z.enum(['pp30', 'pp36', 'pnd1', 'pnd3', 'pnd53', 'cit', 'other']);
export type TaxFormType = z.infer<typeof taxFormType>;

export const taxLedgerInput = z.object({
  kind: taxLedgerKind,
  taxMonth: dateKey.optional(),
  counterparty: z.string().trim().max(300).optional(),
  counterpartyTaxId: z.string().trim().max(30).optional(),
  baseAmount: moneyString.optional(),
  rate: z.coerce.number().min(0).max(100).optional(),
  taxAmount: moneyString,
  vatClaimable: vatClaimability.optional(),
  documentRef: z.string().trim().max(200).optional(),
  notes: z.string().trim().max(1000).optional(),
  /** true = ฝ่ายบัญชียืนยัน → pnl_impact=true + review_status='ok' (ค่าเริ่มต้น false) */
  confirmed: z.boolean().optional(),
  reason: z.string().trim().max(1000).optional(),
});
export type TaxLedgerInput = z.infer<typeof taxLedgerInput>;

export const taxLedgerPatch = taxLedgerInput.partial();
export type TaxLedgerPatch = z.infer<typeof taxLedgerPatch>;

export const taxFilingInput = z.object({
  formType: taxFormType,
  periodYear: z.coerce.number().int().min(2000).max(2100),
  periodMonth: z.coerce.number().int().min(1).max(12).optional(),
  filedDate: dateKey.optional(),
  paidDate: dateKey.optional(),
  amount: moneyString.optional(),
  penalty: moneyString.optional(),
  surcharge: moneyString.optional(),
  status: z.string().trim().max(60).optional(),
  refNo: z.string().trim().max(120).optional(),
});
export type TaxFilingInput = z.infer<typeof taxFilingInput>;

export const whtCertificateInput = z.object({
  certNo: z.string().trim().max(120).optional(),
  whtType: whtType.optional(),
  direction: z.enum(['withheld_by_us', 'withheld_from_us']).optional(),
  counterparty: z.string().trim().max(300).optional(),
  counterpartyTaxId: z.string().trim().max(30).optional(),
  baseAmount: moneyString.optional(),
  rate: z.coerce.number().min(0).max(100).optional(),
  taxAmount: moneyString,
  issueDate: dateKey.optional(),
});
export type WhtCertificateInput = z.infer<typeof whtCertificateInput>;

/** คำขอ reversal ของ financial_entry (T067 adjustment/reversal) */
export const entryReversalInput = z.object({
  originalId: uuid,
  /** วันที่รับรู้ของ reversal (default = event_date ของต้นฉบับ) */
  reversalDate: dateKey.optional(),
  reason: z.string().trim().min(1).max(1000),
});
export type EntryReversalInput = z.infer<typeof entryReversalInput>;
